/**
 * REDIVIDIR o rateio quando a base mudou (ramal a mais, item que entra na base).
 *
 * Contrato (backend): `POST /clients/:id/rateio/simular` NÃO grava; devolve a divisão
 * que a base de hoje daria e o `poolParaConfirmar`. `POST /clients/:id/rateio`
 * grava com esse pool: se a base mudou entre a simulação e o salvar, o backend
 * recusa (400) e é preciso simular de novo. O servidor SEMPRE calcula as cotas:
 * esta rota não aceita fatias declaradas (só a competência passada aceita).
 *
 * ⛔ ECO DA IMPRESSÃO (backend #320): a simulação devolve `impressaoDaSimulacao`, e a gravação EXIGE o
 * mesmo valor de volta em `impressaoParaConfirmar`. O pool sozinho não pegava troca de LISTA (mudar só a
 * base não move o pool). A impressão cobre listas, critério, baseDoPercentual, o flag do desconto e o
 * pool. Por isso o corpo do gravar leva as MESMAS entradas da simulação, e o `baseDoPercentual` não vai em
 * nenhuma das duas: o DTO de simular não o declara (o whitelist o removeria) e o de gravar sim, então
 * mandá-lo só no gravar daria impressões diferentes e um 400 permanente. Sem ele, o backend grava o
 * padrão `pool_compartilhado`, que é a semântica do Redividir.
 *
 * Portão do #300: regravar uma competência que já tem cota acordada devolve 409
 * `cotas_acordadas_divergem`; só prossegue com `recalcularCotas: true`, que na tela
 * é um checkbox nunca marcado por padrão.
 */
import { ItemDoRateio } from './rateioDoGrupo';

/** `POST /clients/:id/rateio/simular`. */
export interface SimulacaoDaDivisao {
  matrizId: number;
  criterio: string;
  descontosSeguemRateio: boolean;
  produtoIds: number[];
  produtoIdsBase: number[];
  pool: number;
  poolParaConfirmar: number;
  /** Eco obrigatório na gravação (backend #320). Ausente em backend anterior ao #320. */
  impressaoDaSimulacao?: string;
  somaFecha: boolean;
  soma: number;
  motivoDoCalculo: string | null;
  mudou: boolean;
  somaAntes: number;
  somaDepois: number;
  base: Array<{ clientId: number; ramais: number }>;
  linhas: LinhaDaSimulacao[];
  /** O motor do rateio está ligado no grupo. Para ESTILO e destaque, nunca para montar frase. */
  motorAtivo?: boolean;
  /** Texto pronto do backend sobre o `totalMedido`; varia com o motor. A tela mostra como vem. */
  totalMedidoRessalva?: string;
  /** Divisão por produto do pool (formato `ItemDoRateio`). */
  itens: ItemDoRateio[];
}

/**
 * Linha da simulação (backend #343, `LinhaDaSimulacaoMedida`). Seis números com nome, e a tela não
 * soma nenhum deles: se faltar um, pede-se ao backend.
 *  - `antes`: valor gravado HOJE dos itens CANDIDATOS (a simulação mede com as listas do corpo). Não é
 *    "o que se paga hoje no rateio atual" quando as listas mudam.
 *  - `depois`: a cota com o conjunto candidato. `delta` = depois − antes.
 *  - `ficaNaAssinatura`: TODO o resto do contrato da empresa, fora do que se divide. `null` = não medido.
 *  - `totalMedido`: o contrato INTEIRO medido (resto + cota). MEDIÇÃO, não garantia. `null` = não medido.
 */
export interface LinhaDaSimulacao {
  clientId: number;
  antes: number;
  depois: number;
  delta: number;
  percentual: number;
  nome?: string;
  ficaNaAssinatura?: number | null;
  totalMedido?: number | null;
}

/** Corpo de `POST /clients/:id/rateio`. */
export interface GravacaoDoRateio {
  competenciaInicio: string;
  poolParaConfirmar: number;
  /** A `impressaoDaSimulacao` da simulação que está sendo gravada. */
  impressaoParaConfirmar?: string;
  criterio: string;
  produtoIds: number[];
  produtoIdsBase: number[];
  baseDoPercentual?: string;
  descontosSeguemRateio?: boolean;
  observacao: string;
  recalcularCotas?: boolean;
}

/** Valor de uma empresa num produto do pool (0 quando ela não participa dele). */
export function valorNoItem(item: ItemDoRateio | undefined, clientId: number): number {
  return Number(item?.porEmpresa.find((p) => p.clientId === clientId)?.valor || 0);
}

/**
 * Pares vigente × calculado por produto do pool, na ordem do vigente; produto que
 * só existe num dos lados também aparece (o outro lado vale 0).
 */
export function produtosLadoALado(vigente: ItemDoRateio[], calculada: ItemDoRateio[]) {
  const ids: number[] = [];
  for (const i of [...vigente, ...calculada]) if (!ids.includes(i.productId)) ids.push(i.productId);
  return ids.map((id) => {
    const v = vigente.find((i) => i.productId === id);
    const c = calculada.find((i) => i.productId === id);
    return { productId: id, nome: v?.name || c?.name || v?.productCode || c?.productCode || `produto ${id}`, vigente: v, calculada: c };
  });
}
